import styles from './ExitSurveyModal.module.css'
import { useTranslations } from 'next-intl'
import IconCheck from '@/assets/img/Icon_Check.svg'
import Icon, { IconColors, IconTypes } from '@/components/UI/Icon/Icon'
import { API_Data_Exit_Survey_Response } from '@/interfaces/api/survey/api_response_types'
import { useSurveyContent } from '@/apiUtilities/queries/useSurveyContent'
import { useEffect } from 'react'
import { useSurveyStore } from '@/stores/useSurveyStore'
import { useModalStore } from '@/stores/useModalStore'
import { useSubmitExitSurvey } from '@/apiUtilities/mutations/useSubmitSurvey'
import { SubmitSurveyPayload } from '@/interfaces/api/survey/api_request_types'
import Survey from '@/components/Survey/Survey'
import { API_Error } from '@/interfaces/api/response/api_response_types'
import { toast } from 'react-toastify'
import Notification from '@/components/Notification/Notification'

interface SurveyModalProps extends React.HTMLAttributes<HTMLDivElement> {
  endpointData: API_Data_Exit_Survey_Response
}

export default function ExitSurveyModal ({ endpointData }: SurveyModalProps) {
  const t = useTranslations('Modals.ExitSurvey')
  const te = useTranslations('Errors')
  const { data: exitSurveyData, isSuccess } = useSurveyContent(endpointData.survey_url!)
  const submitExitSurvey = useSubmitExitSurvey()
  const { fields, retries, setSurvey, removeRetry, setIsPending, reset } = useSurveyStore()
  const { closeModal } = useModalStore()

  /**
   * Handles exit survey API errors and updates UI state accordingly
   *
   * @param response - The API error response returned by the backend
   */
  function handleError (response: API_Error) {    
    setIsPending(false)

    if (retries > 1) removeRetry()
    else {
      reset()
      closeModal()
    }

    toast.error(<Notification text={te('defaultUnknowError')} />)
  }

  /**
   * Runs after a success submit exit survey
   */
  function handleSuccess () {
    reset()
    closeModal()
    toast.success(<Notification text={t('successSubmitMsg')} />)
  }
  
  /**
   * Submit the exit survey and close the modal
   */
  function handleSubmit () {
    setIsPending(true)

    const payload: SubmitSurveyPayload = {
      survey_fields: fields
    }
      
    submitExitSurvey.mutate(payload, {
      onSuccess: handleSuccess,
      onError: (response) => handleError(response)
    })
  }

  /**
   * Initialize the survey after receiving the data from the server
   */
  useEffect(() => {
    if (isSuccess && exitSurveyData) setSurvey(exitSurveyData)
  }, [exitSurveyData])
  
  return (
    <div className={styles.exitSurveyModalWrapper}>
      <div className={styles.exitSurveyMsgWrapper}>
        <Icon
          icon={IconCheck}
          type={IconTypes.SOLID}
          color={IconColors.DARK}
          className={styles.exitSurveyIcon}
        />
        <p className={`bodyTextLarge ${styles.exitSurveyMsgContent}`}>{t('deletedAccountMsg')}</p>
      </div>

      <Survey submit={handleSubmit} />
    </div>
  )
}